import dynamic from 'next/dynamic';
import { usePlausible } from 'next-plausible';
import { useEffect, useState } from 'react';
import { nameFromCC } from 'tf-post-parser';
import { getUser } from '../../../helpers/token';
import { figtree, fraunces } from '../../Landing/fonts';
import { ArrowIcon, PlaneIcon } from '../../Landing/ui';
import FutureImage from '../../FutureImage';

// Signup opens the boarding-pass JoinDialog (via LoginButton), never the
// legacy /join page; lazy so the dialog stack stays out of the post bundle.
const LoginButton = dynamic(
  () => import('../../Header/LoginButton'),
  { ssr: false },
);

// Mid-article conversion module, written and styled as a postcard margin
// note rather than a banner — with a mini browser window showing this very
// story on "your-name.com". Inserted by the parser between top-level
// paragraphs only (never inside custom HTML). Rendered in the static HTML
// for search visitors; removed on mount for logged-in members.
export default function InlineBlogCta({ countryCode, thumbUrl, topic }) {
  const [hidden, setHidden] = useState(false);
  const [joinOpen, setJoinOpen] = useState(false);
  const plausible = usePlausible();

  useEffect(() => {
    if (getUser()) setHidden(true);
  }, []);

  if (hidden) return null;

  let country;
  try {
    country = countryCode ? nameFromCC(countryCode) : undefined;
  } catch {
    country = undefined;
  }

  return (
    <aside
      className={`${figtree.className} tf-inline-promo tw-relative tw-my-12 tw-rounded-xl tw-border tw-border-dashed tw-border-[#C9C0AA] dark:tw-border-[#3A4D44] tw-bg-[#FFFDF6] dark:tw-bg-[#15231E] tw-px-6 tw-py-5 sm:tw-px-7`}
    >
      <div className="tw-flex tw-items-center tw-gap-6">
        <div className="tw-min-w-0 tw-flex-1">
          <div className="tw-flex tw-items-center tw-gap-2 tw-font-mono tw-text-[10px] tw-font-semibold tw-uppercase tw-tracking-[0.25em] tw-text-primary-700 dark:tw-text-primary-300">
            <PlaneIcon className="tw-h-3.5 tw-w-3.5 tw-rotate-90" />A note from
            TravelFeed
          </div>
          <p
            className={`${fraunces.className} tw-mb-0 tw-mt-2.5 tw-max-w-md tw-text-[1.18rem] tw-font-semibold tw-leading-snug tw-tracking-tight tw-text-[#1D2D28] dark:tw-text-[#EBE6D9]`}
          >
            {country ? (
              <>
                Been to{' '}
                <em className="tw-italic tw-text-primary-700 dark:tw-text-primary-300">
                  {country}
                </em>{' '}
                too? Stories like this one live on TravelFeed.
              </>
            ) : topic ? (
              <>
                Got{' '}
                <em className="tw-italic tw-text-primary-700 dark:tw-text-primary-300">
                  {topic}
                </em>{' '}
                stories of your own? They deserve a blog of their own.
              </>
            ) : (
              <>Stories like this one live on TravelFeed.</>
            )}
          </p>
          <div className="tw-mt-4 tw-flex tw-flex-wrap tw-items-center tw-gap-3">
            <button
              type="button"
              onClick={() => {
                plausible('CTA: Inline Promo', { props: { country, topic } });
                setJoinOpen(true);
              }}
              className="tf-btn-outline tw-inline-flex tw-cursor-pointer tw-items-center tw-gap-1.5 tw-whitespace-nowrap tw-rounded-full tw-border tw-border-solid tw-border-primary-600/40 tw-bg-transparent tw-px-4 tw-py-2 tw-text-[13.5px] tw-font-semibold tw-text-primary-700 dark:tw-text-primary-300 tw-no-underline tw-transition-all hover:tw-bg-primary-600 hover:tw-text-white dark:hover:tw-text-white"
            >
              Start your own blog
              <ArrowIcon className="tw-h-3.5 tw-w-3.5" />
            </button>
            <span className="tw-hidden tw-font-mono tw-text-[10px] tw-tracking-wide tw-text-[#8A958F] sm:tw-inline">
              From $19/yr
            </span>
          </div>
        </div>

        {/* Mini browser: this story on your own domain */}
        {thumbUrl && (
          <div
            aria-hidden="true"
            className="tw-hidden tw-w-[10.5rem] tw-shrink-0 tw-rotate-1 sm:tw-block"
          >
            <div className="tw-overflow-hidden tw-rounded-lg tw-border tw-border-solid tw-border-[#E7E0CF] dark:tw-border-[#3A4D44] tw-bg-white dark:tw-bg-[#0F1D18] tw-shadow-[0_14px_30px_-18px_rgba(29,45,40,0.5)]">
              <div className="tw-flex tw-items-center tw-gap-1 tw-border-0 tw-border-b tw-border-solid tw-border-[#E7E0CF] dark:tw-border-[#26382F] tw-bg-[#FAF8F1] dark:tw-bg-[#15231E] tw-px-2 tw-py-1.5">
                <span className="tw-h-1.5 tw-w-1.5 tw-rounded-full tw-bg-[#E8A8A2]" />
                <span className="tw-h-1.5 tw-w-1.5 tw-rounded-full tw-bg-[#EFD49B]" />
                <span className="tw-h-1.5 tw-w-1.5 tw-rounded-full tw-bg-[#A8CCB4]" />
                <span className="tw-ml-1 tw-flex-1 tw-rounded-full tw-bg-white dark:tw-bg-[#0F1D18] tw-px-1.5 tw-text-center tw-font-mono tw-text-[7.5px] tw-leading-relaxed tw-tracking-wide tw-text-[#8A958F]">
                  your-name.com
                </span>
              </div>
              <div className="tw-relative tw-aspect-[16/10]">
                <FutureImage
                  src={thumbUrl}
                  alt=""
                  fill
                  sizes="168px"
                  maxSrcSetWidth={400}
                  className="tw-object-cover"
                />
              </div>
              <div className="tw-space-y-1 tw-px-2.5 tw-py-2">
                <div className="tw-h-1 tw-w-4/5 tw-rounded tw-bg-[#E7E0CF] dark:tw-bg-[#26382F]" />
                <div className="tw-h-1 tw-w-3/5 tw-rounded tw-bg-[#E7E0CF] dark:tw-bg-[#26382F]" />
              </div>
            </div>
          </div>
        )}
      </div>
      {joinOpen && (
        <LoginButton
          open
          hideButtons
          onClickClose={() => setJoinOpen(false)}
        />
      )}
    </aside>
  );
}
